'use client';

import React, { useEffect } from 'react';

import { Box, useSlotRecipe } from '@chakra-ui/react';

import { AdvancedMarker, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';

import { formatHelpers } from '@nocowanie/core/helpers';
import { IconsLinear } from '@nocowanie/icons/linear';

import { useMapSearch } from '@app/hooks/useMapSearch';
import { MarkerTypeEnum } from '@app/models/map-marker.model';
import { ReservationTypeEnum } from '@app/models/reservation-type.model';

import { areMarkerPropsEqual } from './map-advanced-marker.memo';
import { MapAdvancedMarkerProps } from './map-advanced-marker.props';

const MapAdvancedMarkerComponent = ({
    onMarkerClick,
    type,
    id,
    rate,
    currency,
    count,
    isSelected,
    reservationType,
    isUnavailable,
    isInitiallySelected,
    ...rest
}: MapAdvancedMarkerProps) => {
    const [markerRef, marker] = useAdvancedMarkerRef();

    const recipe = useSlotRecipe({ key: 'mapAdvancedMarker' });
    const styles = recipe({
        variant: type,
        selected: type === MarkerTypeEnum.Accommodation && isSelected,
        unavailable: type === MarkerTypeEnum.Accommodation && isUnavailable,
    });

    const { locationDetails } = useMapSearch();

    useEffect(() => {
        if (isInitiallySelected && marker && locationDetails?.details?.accommodationId === id) {
            onMarkerClick(marker);
        }
    }, [isInitiallySelected, marker, locationDetails?.details?.accommodationId]);

    const markerRenderMap: Record<MarkerTypeEnum, () => React.ReactNode> = {
        [MarkerTypeEnum.Accommodation]: () => (
            <Box css={styles.container}>
                {reservationType === ReservationTypeEnum.RealTime && !isUnavailable ? (
                    <IconsLinear.Flash
                        color={'orange.solid'}
                        fill={'orange.solid'}
                        fontSize={'1.15em'}
                        mr={1}
                    />
                ) : null}
                {isUnavailable ? <Box css={styles.circle} /> : null}
                {rate && !isUnavailable ? formatHelpers.price(rate, currency) : null}
            </Box>
        ),

        [MarkerTypeEnum.Cluster]: () => <Box css={styles.container}>{count ?? 1}</Box>,
    };

    return (
        <AdvancedMarker onClick={() => marker && onMarkerClick(marker)} ref={markerRef} {...rest}>
            {markerRenderMap[type]?.() ?? null}
        </AdvancedMarker>
    );
};

export const MapAdvancedMarker = React.memo(MapAdvancedMarkerComponent, areMarkerPropsEqual);
